import React from 'react';
import { Card, Statistic, Typography, Flex, Skeleton } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

const { Text } = Typography;

// Constants
const STYLES = {
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  mainContent: {
    flex: 1,
  },
  titleContainer: {
    marginBottom: 4,
  },
  valueContainer: {
    flex: 1,
  },
  trendIcon: {
    fontSize: '14px',
    marginRight: 8,
  },
  additionalContent: {
    marginTop: 8,
    minHeight: '44px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  contentLine: {
    lineHeight: '20px',
  },
  skeleton: {
    value: { width: 120, height: 32 },
    trend: { width: 40, height: 20 },
    additional: { width: '80%', height: 16 },
  },
};

const DEFAULT_PROPS = {
  precision: 0,
  trendType: 'success',
  bordered: false,
  valueStyle: { fontSize: '28px', fontWeight: 'bold' },
  loading: false,
};

const StatisticCard = ({
  title,
  value,
  suffix,
  precision = DEFAULT_PROPS.precision,
  trend,
  trendType = DEFAULT_PROPS.trendType,
  additionalContent,
  className,
  bordered = DEFAULT_PROPS.bordered,
  valueStyle = DEFAULT_PROPS.valueStyle,
  loading = DEFAULT_PROPS.loading,
  error,
  children,
  dataTestReviewed,
  dataTestTotal,
}) => {
  const renderTrendIcon = () => {
    if (!trend || loading) return null;
    
    const isPositive = trend.startsWith('+');
    const Icon = isPositive ? ArrowUpOutlined : ArrowDownOutlined;
    
    return (
      <Text type={trendType} style={STYLES.trendIcon}>
        <Icon /> {trend}
      </Text>
    );
  };

  const renderSkeletonContent = () => (
    <>
      <Flex justify="space-between" align="start">
        <div style={STYLES.valueContainer}>
          <div style={STYLES.titleContainer}>
            <Text type="secondary" className="ant-statistic-title">
              {title}
            </Text>
          </div>
          <Skeleton.Input active size="small" style={STYLES.skeleton.value} />
        </div>
        <Skeleton.Input active size="small" style={STYLES.skeleton.trend} />
      </Flex>
      <div style={STYLES.additionalContent}>
        <Skeleton.Input active size="small" style={STYLES.skeleton.additional} />
      </div>
    </>
  );

  const renderStatisticContent = () => (
    <>
      <Flex justify="space-between" align="start">
        <div style={STYLES.valueContainer}>
          <div style={STYLES.titleContainer}>
            <Text type="secondary" className="ant-statistic-title">
              {title}
            </Text>
          </div>
          <Statistic
            title=""
            value={value}
            precision={precision}
            valueStyle={valueStyle}
            suffix={suffix}
            data-test={dataTestReviewed}
          />
        </div>
        {renderTrendIcon()}
      </Flex>
      {renderAdditionalContent()}
    </>
  );

  const renderAdditionalContent = () => (
    <div style={STYLES.additionalContent}>
      {error && <Text type="danger">{error}</Text>}
      {!loading && !error && (
        <>
          {renderFormattedContent()}
          {children}
        </>
      )}
    </div>
  );

  const renderFormattedContent = () => {
    if (!additionalContent) return null;

    if (Array.isArray(additionalContent)) {
      return additionalContent.map((content, index) => (
        <div key={index} style={STYLES.contentLine}>
          <Text type="secondary">{content}</Text>
        </div>
      ));
    }

    return (
      <div style={STYLES.contentLine}>
        <Text type="secondary">{additionalContent}</Text>
      </div>
    );
  };

  return (
    <Card bordered={bordered} className={className}>
      <div style={STYLES.cardContent}>
        <div style={STYLES.mainContent}>
          {loading ? renderSkeletonContent() : renderStatisticContent()}
        </div>
      </div>
    </Card>
  );
};

export default StatisticCard; 